import React, { useEffect, useRef } from "react";
import { TopNavProps } from "./TopNav.types";
import {
  TopNavContainer,
  StyledNavIcon,
  NavIcon,
  NavTextWrapper,
  NavArrowWrapper,
  NavIconContainer,
  SupportIconWrapper,
  SupportIcon,
  HeadingWrapper,
} from "./TopNav.styles";
import {
  Body5MediumTypography,
  Heading4SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
import { colorGuide } from "../../theme/colors";
import { Icon } from "../Icon/Icon";
import { Skeleton, Tab } from "@mui/material";
import Tags from "../Tags/Tags";
import Box from "../../primitives/Box/Box";
export const TopNav = ({
  variant,
  title,
  subtitle,
  showBackArrow,
  showEdit,
  showUnhide,
  onClick,
  hideVariant,
  onEditClick,
  onUnhideClick,
  icon,
  statusTag,
  infoTag,
  bgColor,
  isLoading,
  color,
  nonSticky,
  dataCy,
  isWebview = false, // Default to false for webview
  fixed = false,
}: TopNavProps) => {
  const isSupportIconVisible = showEdit || showUnhide || !!icon;
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;

      if (window.scrollY > 0) {
        headerRef.current.style.boxShadow =
          "0 4px 8px -4px rgba(0, 0, 0, 0.08), 0 8px 16px -8px rgba(0, 0, 0, 0.06), 0 3px 6px -3px rgba(0, 0, 0, 0.04)";
      } else {
        headerRef.current.style.boxShadow = "none";
      }
    };

    if (!nonSticky) {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (!nonSticky) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [nonSticky]);
  const renderContent = () => {
    switch (variant) {
      case "logo":
        return (
          <StyledNavIcon
            className="fa-kit fa-wishlink-icon"
            role="img"
            aria-label="Wishlink Logo"
            color={color}
          />
        );
      case "back":
        return (
          <>
            <NavIcon
              className="fa-kit fa-nav-arrow"
              onClick={onClick}
              role="button"
              aria-label="Go Back"
              tabIndex={0}
              color={color}
              data-cy={dataCy}
            />
            {isSupportIconVisible && (
              <SupportIconWrapper>
                {showUnhide && (
                  <Icon
                    onClick={onUnhideClick}
                    height="20"
                    width="20"
                    size="16"
                    name={`fa-sharp fa-solid ${
                      hideVariant === "delete"
                        ? "fa-trash"
                        : hideVariant === "hide"
                        ? "fa-eye-slash"
                        : "fa-eye"
                    }`}
                    color={
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
                {showEdit && (
                  <Icon
                    onClick={onEditClick}
                    height="20"
                    width="20"
                    size="16"
                    name="fa-sharp fa-solid fa-pen"
                    color={
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
              </SupportIconWrapper>
            )}
          </>
        );
      case "close":
        return (
          <SupportIcon
            data-cy={dataCy}
            className="fa-solid fa-xmark"
            onClick={onClick}
            role="button"
            aria-label="Close"
            tabIndex={0}
            color={color}
          />
        );
      case "title-with-status":
        return !showBackArrow && !isSupportIconVisible ? (
          <>
            {isLoading ? (
              <Skeleton width={140} />
            ) : (
              <Heading4SemiboldTypography
                fontFamily="Lexend"
                color={color || "#000"}
              >
                {title}
              </Heading4SemiboldTypography>
            )}
            {isLoading && subtitle ? (
              <Skeleton width={140} />
            ) : (
              <Body5MediumTypography color={color || "#000"}>
                {subtitle}
              </Body5MediumTypography>
            )}
          </>
        ) : (
          <NavArrowWrapper>
            {showBackArrow && (
              <NavIconContainer>
                <NavIcon
                  className="fa-kit fa-nav-arrow"
                  onClick={onClick}
                  role="button"
                  aria-label="Go Back"
                  tabIndex={0}
                  color={color}
                  data-cy={dataCy}
                />
              </NavIconContainer>
            )}

            <NavTextWrapper>
              <HeadingWrapper>
                {isLoading ? (
                  <Skeleton width={140} />
                ) : (
                  <Heading4SemiboldTypography
                    fontFamily="Lexend"
                    color={color || "#000"}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxWidth="16rem"
                  >
                    {title}
                  </Heading4SemiboldTypography>
                )}
                {showEdit && (
                  <Icon
                    onClick={onEditClick}
                    height="20"
                    width="20"
                    size="16"
                    name="fa-sharp fa-solid fa-pen"
                    color={
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
                {icon && (
                  <Box className={icon.iconClassName}>
                    <Icon
                      height="20"
                      width="20"
                      size="20"
                      name={icon.name}
                      state={icon.state || "default"}
                      count={icon.count}
                      notificationColor={icon.notificationColor}
                      countColor={icon.countColor}
                      countTextColor={icon.countTextColor}
                      onClick={icon.onClick}
                      dataCy={`${dataCy}-right-icon`}
                      color={
                        icon.color ||
                        color ||
                        colorGuide.lightComponents.topNav.supportIconColor
                      }
                    />
                  </Box>
                )}
              </HeadingWrapper>

              {isLoading && statusTag ? (
                <Skeleton width={140} />
              ) : (
                <Tags
                  type={statusTag?.type || "track-small"}
                  label={statusTag?.label}
                  backgroundColor={statusTag?.backgroundColor}
                  color={statusTag?.textColor}
                  icon={statusTag?.icon}
                  iconColor={statusTag?.iconColor}
                />
              )}
            </NavTextWrapper>
          </NavArrowWrapper>
        );
      case "title-subtitle":
        return !showBackArrow && !isSupportIconVisible ? (
          <>
            {isLoading ? (
              <Skeleton width={140} />
            ) : (
              <Heading4SemiboldTypography
                fontFamily="Lexend"
                color={color || "#000"}
              >
                {title}
              </Heading4SemiboldTypography>
            )}
            {isLoading && subtitle ? (
              <Skeleton width={140} />
            ) : (
              <Body5MediumTypography color={color || "#000"}>
                {subtitle}
              </Body5MediumTypography>
            )}
          </>
        ) : (
          <NavArrowWrapper>
            {showBackArrow && (
              <NavIconContainer>
                <NavIcon
                  className="fa-kit fa-nav-arrow"
                  onClick={onClick}
                  role="button"
                  aria-label="Go Back"
                  tabIndex={0}
                  color={color}
                  data-cy={dataCy}
                />
              </NavIconContainer>
            )}

            <NavTextWrapper>
              <HeadingWrapper>
                {isLoading ? (
                  <Skeleton width={140} />
                ) : (
                  <Heading4SemiboldTypography
                    fontFamily="Lexend"
                    color={color || "#000"}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxWidth="16rem"
                  >
                    {title}
                  </Heading4SemiboldTypography>
                )}
                {showEdit && (
                  <Icon
                    onClick={onEditClick}
                    height="20"
                    width="20"
                    size="16"
                    name="fa-sharp fa-solid fa-pen"
                    color={
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
                {icon && (
                  <Icon
                    onClick={icon.onClick}
                    height="20"
                    width="20"
                    size="16"
                    name={icon.name}
                    color={
                      color ||
                      icon.color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}

                {infoTag && (
                  <Tags
                    type="whitelist"
                    label={infoTag.label}
                    backgroundColor={infoTag.backgroundColor}
                    color={infoTag.textColor}
                    icon={infoTag.icon}
                    iconColor={infoTag.iconColor}
                    onClick={infoTag.onClick}
                  />
                )}
              </HeadingWrapper>

              {isLoading && subtitle ? (
                <Skeleton width={140} />
              ) : (
                <Body5MediumTypography color={color || "#000"}>
                  {subtitle}
                </Body5MediumTypography>
              )}
            </NavTextWrapper>
          </NavArrowWrapper>
        );
      default:
        return null;
    }
  };

  return (
    <TopNavContainer
      ref={headerRef}
      variant={variant}
      showBackArrow={showBackArrow}
      subtitle={subtitle}
      isSupportIconVisible={isSupportIconVisible}
      bgColor={bgColor}
      sticky={nonSticky}
      isWebview={isWebview}
      fixed={fixed}
    >
      {renderContent()}
    </TopNavContainer>
  );
};
